import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ImageOff, Loader2, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api/client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { cn } from "@/lib/utils";

/** STATUS của pkh_ai_review — khớp với ghi chú trong ai.controller.ts. */
const REVIEW_STATUS = {
  PENDING: 0,
  DONE: 1,
  ERROR: 2,
  IMAGE_ONLY: 3,
} as const;

type Finding = {
  code: string;
  severity: "error" | "warn";
  message: string;
  hint?: string;
};

type ReviewResult = {
  status: number;
  ruleSet: string | null;
  errorCount: number;
  warnCount: number;
  message: string | null;
  findings: Finding[];
  createdDate: string | null;
};

/**
 * Bảng kết quả chấm thể thức (AI) của một file — CHỈ MANG TÍNH THAM KHẢO,
 * không có nút nào ở đây được phép chặn trình ký/ký duyệt.
 *
 * Quan trọng: status === 3 (văn bản dạng ảnh) phải hiện đúng câu "không kiểm
 * tra được thể thức", TUYỆT ĐỐI không được hiện như đã "đạt" — khoảng 16%
 * văn bản rơi vào nhóm này, hiện sai sẽ khiến người ký tin nhầm là đã kiểm.
 */
export function ReviewPanel({ attachId }: { attachId: number }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["ai-review", attachId],
    queryFn: async () => {
      const res = await api.get(`/ai/review/${attachId}`);
      return (res.data?.data ?? res.data) as ReviewResult;
    },
    enabled: Number.isFinite(attachId) && attachId > 0,
    retry: false,
    staleTime: 30_000,
    // Chấm thể thức chạy NGẦM sau khi đính kèm file, xong sau vài giây. Nếu chỉ
    // hỏi một lần lúc mở màn hình thì gần như luôn gặp status 0 ("Chưa kiểm
    // tra") và người dùng phải tự F5 mới thấy kết quả — tưởng là tính năng
    // hỏng. Hỏi lại mỗi 3 giây CHỪNG NÀO còn ở trạng thái chờ, và dừng hẳn khi
    // đã có kết quả cuối (đạt / lỗi / văn bản ảnh) để không gọi API vô hạn.
    refetchInterval: (q) =>
      (q.state.data as ReviewResult | undefined)?.status === 0 ? 3_000 : false,
  });

  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-3">
        <CardTitle>Kiểm tra thể thức (AI)</CardTitle>
        <span className="text-xxs uppercase tracking-wider text-muted-foreground shrink-0">
          Thông tin tham khảo
        </span>
      </CardHeader>
      <CardBody>
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Đang tải kết quả chấm…
          </div>
        )}
        {!isLoading && isError && (
          <div className="text-sm text-muted-foreground">
            Không tải được kết quả chấm thể thức. Không ảnh hưởng tới việc trình ký/ký duyệt.
          </div>
        )}
        {!isLoading && !isError && data && <ReviewBody data={data} />}
      </CardBody>
    </Card>
  );
}

function ReviewBody({ data }: { data: ReviewResult }) {
  if (data.status === REVIEW_STATUS.IMAGE_ONLY) {
    return (
      <div className="flex items-start gap-2.5">
        <ImageOff className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
        <div>
          <div className="text-sm font-medium text-foreground">
            Văn bản dạng ảnh — không kiểm tra được thể thức
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            File là ảnh chụp/scan nên hệ thống chưa đọc được chữ để chấm. Đây KHÔNG phải kết quả
            &quot;đạt&quot; — người trình ký/ký duyệt tự kiểm tra thể thức bằng mắt như trước.
          </p>
        </div>
      </div>
    );
  }

  if (data.status === REVIEW_STATUS.PENDING) {
    return (
      <div className="text-sm text-muted-foreground">
        Chưa có kết quả chấm — văn bản đang chờ xử lý hoặc chưa được đưa vào hàng đợi kiểm tra.
      </div>
    );
  }

  if (data.status === REVIEW_STATUS.ERROR) {
    return (
      <div className="flex items-start gap-2.5">
        <ShieldAlert className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
        <div>
          <div className="text-sm font-medium text-foreground">Không chấm được thể thức</div>
          <p className="text-xs text-muted-foreground mt-1">
            {data.message ?? "Hệ thống gặp lỗi khi kiểm tra — không phải văn bản có lỗi thể thức."}
          </p>
        </div>
      </div>
    );
  }

  // status === DONE
  if (data.errorCount === 0 && data.warnCount === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-foreground">
        <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
        Không phát hiện vấn đề về thể thức.
      </div>
    );
  }

  // Phòng thủ: FINDINGS_JSON đến từ pkh_ai_review, và hợp đồng dữ liệu giữa
  // ai-review.service.ts (ghi) và ai.controller.ts (đọc/phẳng hoá) đã từng vỡ
  // đúng một lần khiến `data.findings` là OBJECT thay vì ARRAY — `.map()` trên
  // object ném TypeError, và trang này KHÔNG có ErrorBoundary bọc ngoài nên
  // lỗi render sẽ làm TRẮNG cả trang /signs/:id. Dữ liệu lạ thì hiện danh
  // sách rỗng, không được để trang vỡ.
  const list = Array.isArray(data.findings) ? data.findings : [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {data.errorCount > 0 && <StatusBadge variant="danger">{data.errorCount} lỗi</StatusBadge>}
        {data.warnCount > 0 && (
          <StatusBadge variant="warning">{data.warnCount} cảnh báo</StatusBadge>
        )}
      </div>
      <ul className="space-y-1.5">
        {list.map((f, i) => (
          <li
            key={`${f.code}-${i}`}
            className={cn(
              "flex items-start gap-2.5 rounded-md border px-3 py-2 text-sm",
              f.severity === "error"
                ? "border-destructive/30 bg-destructive/5"
                : "border-warning/30 bg-warning/10",
            )}
          >
            <AlertTriangle
              className={cn(
                "h-4 w-4 mt-0.5 shrink-0",
                f.severity === "error" ? "text-destructive" : "text-warning",
              )}
            />
            <div className="min-w-0">
              <div className="text-foreground">{f.message}</div>
              {f.hint && <div className="text-xs text-muted-foreground mt-0.5">{f.hint}</div>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
